import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiReact,
  mdiLanguageTypescript,
  mdiLanguageSwift,
  mdiFirebase,
  mdiNodejs,
  mdiShopping,
} from '@mdi/js';
import { Hero } from '../sections/Hero';
import { CTASection } from '../sections/CTASection';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { Container } from '../ui/Container';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
`;

const StorySection = styled(Section)`
  p {
    font-size: var(--text-lg);
    line-height: var(--leading-relaxed);
    max-width: 800px;
    margin-bottom: var(--space-6);

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TechCard = styled(GlassCard)`
  text-align: center;
`;

const TechIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4);
  color: ${props => props.$color};
`;

const TechName = styled.h3`
  font-size: var(--text-lg);
  margin-bottom: var(--space-2);
`;

const TechDescription = styled.p`
  font-size: var(--text-sm);
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(GlassCard)``;

const ValueTitle = styled.h3`
  font-size: var(--text-xl);
  margin-bottom: var(--space-3);
`;

const ValueDescription = styled.p`
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
`;

const technologies = [
  { name: 'React', icon: mdiReact, color: '#61DAFB', description: 'Modern UI development' },
  { name: 'TypeScript', icon: mdiLanguageTypescript, color: '#3178C6', description: 'Type-safe code' },
  { name: 'Swift', icon: mdiLanguageSwift, color: '#F05138', description: 'Native iOS apps' },
  { name: 'Firebase', icon: mdiFirebase, color: '#FFCA28', description: 'Backend services' },
  { name: 'Node.js', icon: mdiNodejs, color: '#339933', description: 'Server-side solutions' },
  { name: 'Shopify', icon: mdiShopping, color: '#7AB55C', description: 'E-commerce platforms' },
];

const values = [
  {
    title: 'Quality First',
    description: 'We never cut corners. Every line of code is written with care, tested thoroughly, and built to last. Quality isn\'t an afterthought - it\'s our foundation.',
  },
  {
    title: 'User-Centered',
    description: 'Great software solves real problems for real people. We obsess over user experience, making complex tasks feel simple and intuitive.',
  },
  {
    title: 'Continuous Growth',
    description: 'Technology evolves, and so do we. We\'re always learning, experimenting, and improving our craft to deliver the best possible solutions.',
  },
];

export function About() {
  return (
    <PageWrapper>
      <PageHead
        title="About Us"
        description="Meet the team behind Lancaster Systems Automation — engineers and designers building thoughtful software for businesses and everyday life."
      />
      <Hero
        eyebrow="About Us"
        title={
          <>
            Passion Meets{' '}
            <span className="gradient">Precision</span>
          </>
        }
        subtitle="Lancaster Systems Automation is where creativity meets technical excellence. We build software that matters - apps that solve problems and websites that drive results."
        primaryCTA={{ label: 'Work With Us', to: '/contact' }}
        secondaryCTA={{ label: 'View Our Work', to: '/portfolio' }}
        showStats={false}
      />

      <StorySection>
        <Container size="lg">
          <SectionHeader title="Our Story" />
          <p>
            Lancaster Systems Automation started with a simple idea: build software the right way.
            No shortcuts, no compromises, just clean code and thoughtful design.
          </p>
          <p>
            What began as personal projects - tools to manage our own daily life - evolved into
            a full ecosystem of apps. Charque for meal planning, Inventory for organizing belongings,
            When for coordinating events, and The Bestagon for when you need a break.
          </p>
          <p>
            Along the way, we discovered that the same principles that made our apps successful
            could help businesses succeed too. Today, we combine our product expertise with
            client services, helping companies build the digital experiences they need.
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
            Lancaster Systems Automation is a DBA of Data 26 Solutions LLC.
          </p>
        </Container>
      </StorySection>

      <Section background="secondary">
        <SectionHeader
          title="Our Values"
          subtitle="The principles that guide everything we build."
          centered
        />
        <ValuesGrid>
          {values.map(value => (
            <ValueCard key={value.title}>
              <ValueTitle>{value.title}</ValueTitle>
              <ValueDescription>{value.description}</ValueDescription>
            </ValueCard>
          ))}
        </ValuesGrid>
      </Section>

      <Section>
        <SectionHeader
          title="Our Tech Stack"
          subtitle="Modern tools for modern problems."
          centered
        />
        <TechGrid>
          {technologies.map(tech => (
            <TechCard key={tech.name} hover>
              <TechIcon $color={tech.color}>
                <Icon path={tech.icon} size={1.3} />
              </TechIcon>
              <TechName>{tech.name}</TechName>
              <TechDescription>{tech.description}</TechDescription>
            </TechCard>
          ))}
        </TechGrid>
      </Section>

      <CTASection
        title="Let's Build Something Together"
        subtitle="Whether you're looking for a custom app or want to improve your digital presence, we're here to help."
        primaryCTA={{ label: 'Get in Touch', to: '/contact' }}
        secondaryCTA={{ label: 'View Our Services', to: '/services' }}
      />
    </PageWrapper>
  );
}

export default About;
